import { defineStore } from "pinia";
import { userApi } from "~/api/user";
import type { IUserEquipment } from "~/common/interfaces/IUser";
import { MarketplaceApi } from "~/api/marketplace";
import type { IAuctionItem, IMetaData, IPagination } from "~/common/interfaces/IMarketplace";
import mitt from "mitt";
import type { ITransaction } from "~/common/interfaces/ITransaction";
import { CardType, MarketType, NftSkill, NftType, Pagination } from "fungi-types";
import { type RemovableRef, useLocalStorage } from "@vueuse/core";
import { useRouter } from "vue-router";
import { useStoreUserV2 } from "./storeUserV2";
import { auctionApi } from "~/api/auction";
import { MarketPlaceFilter } from "~/api/auction/types";

export const useStoreDeck = defineStore("deck-store", {
  state: () => ({
    marketplaceApi: new MarketplaceApi(MarketType.SECONDARY),
    items: [] as NftSkill[],
    equipmentItems: [] as IUserEquipment["data"],
    pagination: {
      page: 1,
      limit: 20,
    } as Pagination,
    identifier: null as string | null, // Wallet address
    loading: false,
    selected: {} as NftSkill,
    filters: {} as Partial<MarketPlaceFilter>,
    activities: [] as ITransaction[],
    metaActivities: {} as IMetaData,
    loadingActivities: false,
    emitter: mitt(),
    f2pDeckId: "",
    type: useLocalStorage("deckNftType", NftType.SKILL) as RemovableRef<NftType>,
  }),
  getters: {
    activeFilters(state) {
      return Object.values(state.filters).reduce((count: number, value) => {
        if (
          ((typeof value === "string" || Array.isArray(value)) && value.length > 0) ||
          (typeof value === "boolean" && value === true)
        ) {
          return count + 1;
        }
        return count;
      }, 0);
    },
  },

  actions: {
    async getUserDeck(filters?: Partial<MarketPlaceFilter>) {
      this.loading = true;
      try {
        if (this.type === NftType.F2P) {
          const nfts = await auctionApi.nfts.myDeck.query({
            page: 1,
            f2pDeckId: this.f2pDeckId,
            ...(filters || this.filters),
          });
          this.items = nfts.result;
          this.pagination = nfts.pagination;
        } else if (this.type === NftType.SKILL) {
          const nfts = await auctionApi.nfts.myDeck.query({
            page: 1,
            ...(filters || this.filters),
          });
          this.items = nfts.result;
          this.pagination = nfts.pagination;
        }
        this.loading = false;
      } catch (e) {
        console.log("e", e);
      }
      this.loading = false;
      return this.items;
    },
    async clearDeck() {
      this.items = [];
      this.equipmentItems = [];
      this.pagination = {
        page: 1,
        limit: 20,
      } as Pagination;
    },
    async clearAll() {
      this.items = [];
      this.equipmentItems = [];
      this.pagination = {
        page: 1,
        limit: 20,
      } as Pagination;
      this.selected = {} as NftSkill;
      this.identifier = null;
      this.loading = false;
      this.filters = {} as MarketPlaceFilter;
      this.setType(NftType.SKILL);
    },
    async getUserEquipment(walletOwner: string, pagination?: IPagination) {
      const response = await userApi.getUserEquipment(walletOwner, this.filters, pagination);
      this.equipmentItems = response.data;
    },
    setType(newType: NftType) {
      this.type = newType;
    },

    setF2pDeckId(deckId: string) {
      this.f2pDeckId = deckId;
    },

    updateItem(tokenId: number, data: Partial<NftSkill>) {
      let index = -1;
      if (data.cardType === CardType.NftSkills) {
        index = this.items.findIndex((item) => item.tokenId === tokenId);
        this.items[index] = { ...this.items[index], ...data } as NftSkill;
      }
    },

    sendTologin() {
      const router = useRouter();
      router.push({ name: "SignIn" });
    },

    updateItemAuction(tokenId: number, data: Partial<IAuctionItem>) {
      this.$patch((state) => {
        const item = state.items.find((item) => item.tokenId === tokenId);
        if (!item || !item.auction) return;
        const { status } = data;
        if (status) Object.assign(item.auction, { status });
      });
    },

    async nextPage() {
      this.loading = true;
      if (this.pagination.hasNextPage) {
        if (this.type === NftType.F2P) {
          const response = await auctionApi.nfts.myDeck.query({
            page: this.pagination.page + 1,
            limit: this.pagination.limit,
            f2pDeckId: this.f2pDeckId,
            ...this.filters,
          });
          this.items.push(...response.result);
          this.pagination = response.pagination;
        } else if (this.type === NftType.SKILL) {
          const response = await auctionApi.nfts.myDeck.query({
            page: this.pagination.page + 1,
            limit: this.pagination.limit,
            ...this.filters,
          });
          this.items.push(...response.result);
          this.pagination = response.pagination;
        }
      }
      this.loading = false;
    },

    async unList(cuid: IAuctionItem["cuid"], tokenId: number, cardType: CardType) {
      try {
        const userStore = useStoreUserV2();
        if (userStore.isConnected) {
          await auctionApi.auctions.secondary.cancel.mutate({ auctionCuid: cuid });
          this.updateItem(tokenId, { auction: undefined, cardType });
        }
      } catch (e) {
        this.sendTologin();
      }
    },

    async getActivities() {
      const userStore = useStoreUserV2();
      if (userStore.isConnected) {
        this.loadingActivities = true;
        const response = await userApi.getActivity();
        [this.activities, this.metaActivities] = [response.data, response.meta];
        this.loadingActivities = false;
      } else {
        this.sendTologin();
      }
    },

    async selectPageActivities(page: number) {
      const userStore = useStoreUserV2();
      if (userStore.isConnected) {
        this.loadingActivities = true;
        const response = await userApi.getActivity({ page });
        [this.activities, this.metaActivities] = [response.data, response.meta];
        this.emitter.emit("activity-page", page);
        this.loadingActivities = false;
      } else {
        this.sendTologin;
      }
    },

    async updateFilters(filters: Partial<MarketPlaceFilter>) {
      this.filters = { ...filters };
      await this.getUserDeck();
    },

    async resetFilters() {
      this.filters = {} as MarketPlaceFilter;
      await this.getUserDeck();
    },
  },
});
